import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Phone, CheckCircle2, PackageOpen, HardHat, SunMedium, Wrench, ShieldCheck } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { HeroBackground } from "@/components/HeroBackground";
import { CTABlock } from "@/components/CTABlock";
import { SectionDecor } from "@/components/SectionDecor";
import { Button } from "@/components/ui/button";
import { ServiceTile } from "@/components/ServiceTile";
import { solarTiles } from "@/data/serviceTiles";
import { company } from "@/data/site";
import heroImg from "@/assets/service-heroes/solar-panel-removal-reinstallation.jpg";

const CANONICAL = "https://mckay.steadyautogrowthdemo.com/services/solar-panel-removal-reinstallation";

export const Route = createFileRoute("/services/solar-panel-removal-reinstallation")({
  head: () => ({
    meta: [
      { title: "Solar Panel Removal & Reinstallation in Southern California – McKay Roofing & Solar" },
      { name: "description", content: "Need a re-roof under existing solar? We detach panels, replace the roof, and reinstall your array – one crew, one warranty, across Southern California." },
      { name: "keywords", content: "solar panel removal and reinstallation, remove solar panels for roof replacement, detach and reset solar panels, solar removal cost Southern California, McKay Roofing & Solar" },
      { property: "og:title", content: "Solar Panel Removal & Reinstallation – McKay Roofing & Solar" },
      { property: "og:description", content: "One crew handles the roof and the solar – panels detached, roof replaced, array put back the right way." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: `https://mckay.steadyautogrowthdemo.com${heroImg}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://mckay.steadyautogrowthdemo.com${heroImg}` },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Solar Panel Removal and Reinstallation",
          provider: { "@type": "RoofingContractor", name: "McKay Roofing & Solar", telephone: company.phone },
          areaServed: { "@type": "AdministrativeArea", name: "Southern California" },
          url: CANONICAL,
          description: "Detach and reset service for solar arrays during roof replacement in Southern California.",
        }),
      },
    ],
  }),
  component: RemovalReinstallPage,
});

const STEPS = [
  {
    icon: HardHat,
    kicker: "Step 01",
    title: "Site walk & array assessment",
    body:
      "We start on the roof, not on paper. Our team inspects the existing array, wiring, roof condition and attachment points, then maps out exactly how the panels come off and where they go while the roof is open.",
  },
  {
    icon: PackageOpen,
    kicker: "Step 02",
    title: "Careful detach & label",
    body:
      "Every panel is labeled, disconnected and staged on padded racks – never stacked on the driveway. Micro-inverters, rails and conduit are removed together so nothing gets bent, scratched or misplaced.",
  },
  {
    icon: Wrench,
    kicker: "Step 03",
    title: "Re-roof done right",
    body:
      "With the array off, our roofing crew replaces the roof properly – decking, underlayment, flashing and ventilation – instead of working around panels. This is the difference between a 5-year fix and a 25-year roof.",
  },
  {
    icon: SunMedium,
    kicker: "Step 04",
    title: "Reinstall & recommission",
    body:
      "Panels go back on new flashings and mounts, wiring is redone cleanly, and the system is recommissioned. We verify production against your pre-removal baseline so you know it's back to full output.",
  },
  {
    icon: ShieldCheck,
    kicker: "Step 05",
    title: "One warranty, one call",
    body:
      "Because the same crew handles the roof and the solar, there's no finger-pointing later. Roof workmanship, panel attachments and system performance all sit with McKay – one number to call if anything ever comes up.",
  },
];

function RemovalReinstallPage() {
  const related = solarTiles.filter((s) => s.slug !== "solar-panel-removal-reinstallation").slice(0, 4);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <ImmersiveHero />
        <QuickAnswer />
        <ProcessTimeline />
        <WhyCombined />
        <RelatedGrid tiles={related} />
        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}

function ImmersiveHero() {
  return (
    <section className="relative min-h-[92vh] w-full overflow-hidden bg-primary text-primary-foreground">
      <HeroBackground src={heroImg} alt="McKay crew carefully removing solar panels from a tile roof in Southern California" overlay="black-soft" />

      <div className="relative z-10 mx-auto flex min-h-[92vh] w-full max-w-7xl flex-col justify-end px-4 pb-16 pt-32 sm:px-6 sm:pb-20 lg:px-8 lg:pb-28 animate-fade-in">
        <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground backdrop-blur">
          <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
          Roofing + Solar, One Crew
        </div>
        <h1 className="mt-6 max-w-4xl text-5xl font-extrabold leading-[1.02] sm:text-6xl lg:text-7xl text-balance">
          Solar Panel Removal <span className="text-secondary">&</span> Reinstallation
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-primary-foreground/85 sm:text-xl">
          The right way to re-roof under an existing solar array in Southern California – panels off, roof replaced, array put back exactly where it belongs.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/contact">Get a Free Estimate <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20">
            <a href={`tel:${company.phone.replace(/[^0-9+]/g, "")}`}><Phone className="mr-2 h-4 w-4" />{company.phone}</a>
          </Button>
        </div>

        {/* Stat strip */}
        <div className="mt-14 grid max-w-3xl grid-cols-3 gap-6 border-t border-white/20 pt-6">
          {[
            { k: "1981", v: "Family-owned since" },
            { k: "C-39 · C-46", v: "Licensed roofing + solar" },
            { k: "1 crew", v: "Roof and array" },
          ].map((s) => (
            <div key={s.k}>
              <div className="text-2xl font-extrabold text-secondary sm:text-3xl">{s.k}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-primary-foreground/70 sm:text-sm">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function QuickAnswer() {
  return (
    <section className="relative overflow-hidden bg-background">
      <SectionDecor variant="shingles" side="right" tone="primary" opacity={0.05} />
      <div className="relative mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">The short version</div>
        <p className="mt-4 text-2xl font-medium leading-snug text-foreground sm:text-3xl text-balance">
          If your roof is aging under an existing solar system, you have two choices – patch around the panels and pay twice later, or take the array down, replace the roof properly, and put solar back on top of a roof built to last another quarter century. We do the second one.
        </p>
      </div>
    </section>
  );
}

function ProcessTimeline() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      const total = rect.height - vh * 0.6;
      const scrolled = Math.min(Math.max(-rect.top + vh * 0.2, 0), total);
      setProgress(total > 0 ? scrolled / total : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section ref={containerRef} className="relative overflow-hidden bg-primary text-primary-foreground">
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_20%_20%,hsl(var(--secondary))_0,transparent_45%),radial-gradient(circle_at_80%_75%,hsl(var(--secondary))_0,transparent_50%)]" />
      <div className="relative mx-auto max-w-6xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary">Our Process</div>
          <h2 className="mt-3 text-3xl font-extrabold sm:text-4xl">Detach. Re-roof. Reinstall.</h2>
          <p className="mt-4 text-primary-foreground/80">
            Five checkpoints from the first walkthrough to the final production check – coordinated by a single McKay crew.
          </p>
        </div>

        <div className="relative grid gap-10 lg:grid-cols-[80px_1fr]">
          {/* Rail */}
          <div className="relative hidden lg:block">
            <div className="sticky top-24 h-[70vh] w-[3px] rounded-full bg-primary-foreground/15">
              <div
                className="absolute inset-x-0 top-0 rounded-full bg-secondary transition-[height] duration-150 ease-out"
                style={{ height: `${Math.round(progress * 100)}%` }}
              />
            </div>
          </div>

          <ol className="space-y-8">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <li
                  key={step.title}
                  className="group relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-secondary/50 hover:bg-white/[0.07] sm:p-8"
                  style={{ animation: `stepIn 700ms ${i * 80}ms ease-out both` }}
                >
                  <div className="flex items-start gap-5">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-secondary text-secondary-foreground shadow-lg shadow-secondary/20">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-secondary">{step.kicker}</div>
                      <h3 className="mt-1 text-2xl font-extrabold leading-tight">{step.title}</h3>
                      <p className="mt-3 text-primary-foreground/80">{step.body}</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      <style>{`
        @keyframes stepIn { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </section>
  );
}

function WhyCombined() {
  const bullets = [
    "No coordinating a separate solar company with your roofer – we handle both trades in-house.",
    "Roof is replaced the right way: full underlayment, proper flashing, correct ventilation, no shortcuts.",
    "New attachment points and flashings mean fewer leak callbacks a few years down the road.",
    "Panels are staged and stored safely on-site so nothing gets damaged during the re-roof.",
    "System is recommissioned and production is verified against your pre-removal baseline.",
    "One warranty covering the roof, the attachments, and the solar reinstallation.",
  ];
  return (
    <section className="relative overflow-hidden bg-muted/40">
        <SectionDecor variant="panel" side="left" tone="primary" opacity={0.04} />
      <div className="relative mx-auto max-w-6xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Why McKay</div>
            <h2 className="mt-3 text-3xl font-extrabold text-primary sm:text-4xl text-balance">
              One team on the roof – from the first panel off to the last one back on.
            </h2>
            <p className="mt-5 text-muted-foreground">
              Most solar companies won't touch your roof. Most roofers won't touch your panels. We've been doing both since long before that was normal – which means your project doesn't stall waiting for the other guy.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/contact">Talk to Our Team</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/services/roof-replacement">See Roof Replacement</Link>
              </Button>
            </div>
          </div>
          <ul className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                <span className="text-sm text-foreground sm:text-base">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function RelatedGrid({ tiles }: { tiles: typeof solarTiles }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <div className="mb-10 flex items-end justify-between gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Related Services</div>
          <h2 className="mt-2 text-3xl font-extrabold text-primary sm:text-4xl">Other Solar Services</h2>
        </div>
        <Link to="/services/solar" className="hidden text-sm font-semibold text-accent hover:underline sm:inline">
          View all solar services →
        </Link>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <ServiceTile key={t.slug} item={t} />
        ))}
      </div>
    </section>
  );
}
